import type { UpdateRequestUnion } from "@nlc/shared-types";
import type { UseFormReturn } from "react-hook-form";

import {
  BriefCreatorTypeEnum,
  RequestTypeEnum,
  updateRequestUnionType,
} from "@nlc/shared-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { useCapturePosthogEvent } from "@/hooks/posthog/useCapturePosthogEvent";
import { requestListKeys } from "@/hooks/queryKeys";
import authenticatedApi from "@/lib/api/client";

import type { CreateGraphicRequestFormData } from "../components/useCreateGraphicRequestForm";

import { getValidAttachmentIds } from "../../shared/utils";

type UseSaveDraftMutationProps = {
  draftId: number | undefined;
  formMethods: UseFormReturn<CreateGraphicRequestFormData>;
};

export function useSaveDraftMutation({
  draftId,
  formMethods,
}: UseSaveDraftMutationProps) {
  const capture = useCapturePosthogEvent();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: CreateGraphicRequestFormData) => {
      if (!draftId) throw new Error("No request ID found");

      const commonFields: UpdateRequestUnion = {
        attachmentIds: [
          ...getValidAttachmentIds(formData.assets),
          ...getValidAttachmentIds(formData.inspirations),
        ],
        brandId: formData.brand ?? undefined,
        briefCreatorType: formData.briefCreatorType,
        collaboratorIds: formData.collaboratorIds,
        complexityId: formData.designComplexity,
        creativeDirection: formData.creativeDirection,
        designTypeId: formData.designType,
        name: formData.requestName,
        preferredDesignerIds: formData.preferredDesignerIds,
        preferredFileType: formData.fileTypes,
        requestType: RequestTypeEnum.GRAPHIC,
        restrictAiAssets: formData.restrictAiAssets,
        sizesNeeded: formData.sizes,
      };

      const mbcFields = formData.briefCreatorDetailed;

      const briefTypeFields =
        formData.briefCreatorType === BriefCreatorTypeEnum.DETAILED
          ? {
              briefText: mbcFields.finalBriefHTML,
              description: formData.briefCreatorSingle.requestDescriptionHTML,
              textForDesign: formData.briefCreatorSingle.textsForDesignHTML,
            }
          : {
              briefText: undefined,
              description: formData.briefCreatorSingle.requestDescriptionHTML,
              textForDesign: formData.briefCreatorSingle.textsForDesignHTML,
            };

      const magicalBriefCreatorFields =
        formData.briefCreatorType === BriefCreatorTypeEnum.DETAILED
          ? {
              magicalBriefCreator: {
                additionalGuidelines: mbcFields.additionalGuidelines,
                brandGuidelines: mbcFields.brandGuidelines,
                creativeGoals: mbcFields.creativeGoal,
                keyMessageAndCta: mbcFields.keyMessageAndCta,
                scope: mbcFields.scope,
              },
            }
          : {};

      const parsed = updateRequestUnionType.parse({
        ...commonFields,
        ...briefTypeFields,
        ...magicalBriefCreatorFields,
      });

      return authenticatedApi
        .url(`/requests/${draftId}`)
        .patch(parsed)
        .json<{ message: string; request_data: unknown }>();
    },
    onError: (error) => {
      console.error("Failed to save draft:", error);

      toast.add(
        {
          description: error.message,
          title: "Error saving draft",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    },
    onSuccess: () => {
      capture("brief_draft_saved", {
        draft_id: draftId!,
        form_version: formMethods.getValues("briefCreatorType"),
      });

      queryClient.invalidateQueries({ queryKey: requestListKeys.all });

      toast.add(
        {
          title: "Draft saved successfully",
          type: "success",
        },
        {
          timeout: 5000,
        },
      );
    },
  });
}
